import * as React from 'react';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import { generatePath } from "react-router";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { Button } from "react-bootstrap";
import moment from 'moment';
import _, { min } from "lodash";
import { Definition } from "../../../../../../data/Definition";

import '../../../../../../styles/mapbox-gl.css'
import '../../../../styles/FieldManager.scss';

import MapGL, { Source, Layer, NavigationControl, ScaleControl, Popup } from 'react-map-gl'; 
import { Size, useSize } from 'react-hook-size';
import { ViewState } from 'react-map-gl-draw/node_modules/@types/react-map-gl';

import ESRI_BASE from '../../../../components/Map/styles/esri-base.json';
import ESRI_BASE_LABELS from '../../../../components/Map/styles/esri-base-labels.json';
import { clusterLayer, fieldsBorderLayer, zonesBorderLayer, fieldsHighlightLayer, fieldsLayer, zonesLayer, fieldsSelectedBorderLayer, unclusteredLayer } from '../../../../components/Map/styles/fields';
import { stateBorderLayer } from '../../../../components/Map/styles/borders';
import { getTileUrlTemplate } from "../../../../../fields/utils/dataSourceUtils";
import FieldDownload from './FieldDownload';
import FieldList from './FieldList';
import { zoomToFeature, zoomToFeatures, createFeatureFromField, createFeatureCollection, getViewport, createCentroidFeatureFromField, createCentroidFeatureFromGeom } from '../../../../utils/mapUtils';
import { DataSourceLayerMeta, DataSourceRasterLayer, Field, LayerVisibility } from '../../../../types/types';
import ZoomExtentTool from '../ZoomExtentTool';
import LayerVisibilityControlPanel from '../LayerVisibilityControlPanel';
import { useFieldStore } from '../../providers/FieldProvider';
import { useRasterDataSources } from '../../../../hooks/useRasterDataSources';
import { useStore } from '../../../../providers/AppProvider';
import { useUnitConversion, formatUnitAbbreviation} from '../../../../utils/unitConversion';
import CA_BOUNDARY from '../../../../data/ca_boundary.json';
import InfoDrawer from '../../../../components/Elements/InfoDrawer';
import SoilInfoWidget from './SoilInfoWidget';
import { useLayerStore } from '../../providers/LayerProvider';
import DwFieldStatWidget from './DwStatWidget';
import DwZoneStatWidget from './DwZoneStatWidget';
import FieldStatTable from './FieldStatTable';
import ZoneManagementSelector from './ZoneManagementSelector';
import ZoneManagementBBCard from './ZoneManagementBBCard';
import { Zone } from '../../../../types/types';
import { 
    BI_DATA_SOURCE, 
    NAIP_DATA_SOURCE, 
    ESRI_DATA_SOURCE, 
    MAPBOX_DATA_SOURCE, 
    ESRI_DATA_SOURCE_LABELS,
    PGI_DATA_SOURCE, 
    PRODUCTIVITY_DESC, 
    STRESS_DESC, 
    VVIC_DATA_SOURCE,
    CCCI_DATA_SOURCE,
    TOANDRE_DATA_SOURCE,
    defaultViewport 
} from '../../../../constants';

export const ShowFields = () => {
    const FIELD_APP_URL = process.env.REACT_APP_FIELD_MGR_ROOT_URL;
    
    //const { orgId } = useStore();
    let orgId = "fd8ce854-9f8e-11ea-a22c-02a6f2671be5"
    const RASTER_DATA_SOURCES: DataSourceLayerMeta[] = [{
            id: `${orgId}${VVIC_DATA_SOURCE}`,
            type: 'overlay',
            order: 1,
            visible: false,
            minZoom: 10,
            maxZoom: 24
        },{
            id: `${orgId}${PGI_DATA_SOURCE}`,
            type: 'overlay',
            order: 2,
            visible: false,
            minZoom: 10,
            maxZoom: 24
        },{
            id: `${orgId}${TOANDRE_DATA_SOURCE}`,
            type: 'overlay',
            order: 3,
            visible: false,
            minZoom: 10,
            maxZoom: 24
        }/*,{
            id: `${orgId}${CCCI_DATA_SOURCE}`,
            type: 'overlay',
            order: 4,
            visible: false,
            minZoom: 10,
            maxZoom: 24
        }*/,{
            id: NAIP_DATA_SOURCE,
            type: 'base',
            name: 'Historical Imagery',
            order: 4,
            visible: false,
            minZoom: 10,
            maxZoom: 24
        },{
            id: MAPBOX_DATA_SOURCE,
            type: 'base',
            name: 'Mapbox Imagery',
            order: 5,
            visible: true,
            minZoom: 0,
            maxZoom: 24
        },{
            id: ESRI_DATA_SOURCE_LABELS,
            type: 'base',
            name: 'Labels',
            order: 0,
            visible: true,
            minZoom: 0,
            maxZoom: 18
        },{
            id: `${orgId}${BI_DATA_SOURCE}`,
            type: 'overlay',
            order: 3,
            visible: false,
            minZoom: 10,
            maxZoom: 24
        }
    ];

    const history = useHistory();
    const match = useRouteMatch(`${FIELD_APP_URL}/:vp/fields`);

    const mapContainerRef = useRef(null);
    const { width, height } = useSize(mapContainerRef);

    const { vp, field } = useParams();

    const { dataSources, loading } = useRasterDataSources(RASTER_DATA_SOURCES);
    //const { dataSources: animationDataSources, loading: loadingAnimationDataSources } = useRasterDataSources(RASTER_DATA_SOURCES);

    const { fields } = useFieldStore();

    const { rasterLayers, clearLayers } = useLayerStore();

    const [map, setMap] = useState<any | null>(null);

    const [fieldsDataSource, setFieldsDataSource] = useState(null);

    const [zonesDataSource, setZonesDataSource] = useState(null);
    const [zonesPointDataSource, setZonesPointDataSource] = useState(null);
    const [zoneDataSourceIncrementor, setZoneDataSourceIncrementor] = useState(0);

    const [zones, setZones] = useState<Zone[] | null>(null);
    const [rightInfoDrawerOpen, setRightInfoDrawerOpen] = useState<boolean>(true);

    const [zonesVisibility, setZonesVisibility] = useState<boolean>(true);

    const [zoneData, setZoneData] = useState<any | null>(null);
    const [zoneDefinition, setZoneDefinition] = useState<Definition | null>(null);
    const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
    const [selectedZoneSetId, setSelectedZoneSetId] = useState<string | null>(null);
    const [selectedZoneSetIndex, setSelectedZoneSetIndex] = useState<number>(0);
    const [selectedZoneName, setSelectedZoneName] = useState<string | null>(null);
    const [centroidDataSource, setCentroidDataSource] = useState(null);
    const [pauseClickEvents, setPauseClickEvents] = useState(false);

    const [dateSelectorParameters, setDateSelectorParameters] = useState<any>(null);
    const [dateIndexSelected, setDateIndexSelected] = useState<Array<number>>([0,0,0]);
    const [dateSelected, setDateSelected] = useState<string | null>(null);
    
    const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
    const [selectedField, setSelectedField] = useState<Field | null>(null);
    const [selectedFieldIncrement, setSelectedFieldIncrement] = useState<number>(0);
    const [hoverFieldId, setHoverFieldId] = useState<number | null>(null);
    const [hoverInfo, setHoverInfo] = useState<any>(null);
    const [showHighlightLayer,] = useState<boolean>(true);
    const [initViewportStr,] = useState(vp);
    const [overlayVisibility, setOverlayVisible] = useState<LayerVisibility[]>([]);
    const [overlayVisibilityMapControl, setOverlayVisibleMapControl] = useState<LayerVisibility[]>([]);
    const [rasterDataSources, setRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [animationRasterDataSources, setAnimationRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [datesAvailable, setDatesAvailable] = useState<Array<string>>([])
    const [productivityGlobalMaxMin, setProductivityGlobalMaxMin] = useState<any>({max:null, min:null});

    const [timelineStatus, setTimelineStatus] = useState<string>("");
    const [activeAnimation, setActiveAnimation] = useState<string>("not engaged"); // "not engaged" | "playing" | "paused"

    const [loadedTiles, setLoadedTiles] = useState<boolean>(true);

    //const [viewport, setViewport] = useState<ViewState>(getViewport(vp, null, null, null, "B"));
    const [viewport, setViewport] = useState(() => getViewport(vp, null, null, null, "B"));

    const [initialViewportSet, setInitialViewportSet] = useState<boolean>(false);

    const [timerInterval, setTimerInterval] = useState<number>(0);
    const [indexA, setIndexA] = useState<number>(0);

    const [animationInterval, setAnimationInterval] = useState<any>(undefined);

    const [size, setSize] = useState<Size>({
        width: width || 800,
        height: height || 800,
    });

    const [showDownload, setShowDownload] = useState(false);
    const [preferredLenSmall, setPreferredLenSmall] = useState<any>(null);
    const [preferredLenAbbr, setPreferredLenAbbr] = useState<any>("in");
    const { localePreferences } = useStore();
    const { convertToPreferredUnitValue, convertToPreferredUnitString, unitsConvert } = useUnitConversion();
    
    const { socket, updateFieldSelected, updateLayersForExport, setDownloadDateRange, settings, state} = useStore();
    
    useEffect(() => {
        const _preferredLenSmall = _.get(localePreferences, 'measureUnits.length.small');
        const _preferredLenAbbr: string =
            typeof _preferredLenSmall === 'string' ? _preferredLenSmall : _preferredLenSmall?.abbr || 'in';
        setPreferredLenSmall(_preferredLenSmall);
        setPreferredLenAbbr(_preferredLenAbbr);
    }, [localePreferences]);

    useEffect(() => {
        console.log("032224 field", field);
    }, [field]);
    useEffect(() => {
        console.log("A041524 rasterDataSources",rasterDataSources);
        console.log("A041524 animationRasterDataSources", animationRasterDataSources);
        console.log("A041524 overlayVisibility", overlayVisibility);
    }, [animationRasterDataSources, rasterDataSources, overlayVisibility]);
    useEffect(() => {
        console.log("032824 state", state);
        console.log("032824 state.definitions", state.definitions);
        const definition = state.definitions.find((d:Definition) => d.name.toLowerCase() == 'zone') as Definition;
        console.log("032824 definition", definition);
        setZoneDefinition(definition);
    }, [state]);

    useEffect(() => {
        console.log("C060424 fields", fields);
        if (fields?.length > 0 && !initialViewportSet) {
            console.log("013024 viewport", viewport);
            console.log("013024 defaultViewport", defaultViewport);
            if (viewport.latitude === defaultViewport.latitude && viewport.longitude === defaultViewport.longitude && viewport.zoom === defaultViewport.zoom) {
                console.log("013024 fields", fields);
                //console.log("013024 viewport", viewport);
                const _viewport =  getViewport(vp, fields, width, height, "A")
                setViewport(_viewport);
                setInitialViewportSet(true);
                const str = `@${_viewport.latitude.toFixed(5)},${_viewport.longitude.toFixed(5)},${_viewport.zoom.toFixed(2)}`;
                const url = field ?
                    generatePath(`${FIELD_APP_URL}/:vp/fields/:field`, { vp: str, field: field }) :
                    generatePath(`${FIELD_APP_URL}/:vp/fields`, { vp: str });
    
                delayedUrlUpdate(url);
            }
        }
    }, [fields]);

    useEffect(() => {
        console.log("083023 socket", socket);
    }, [socket]);

    useEffect(() => {
        console.log("A050324 overlayVisibilityMapControl", overlayVisibilityMapControl);
    }, [overlayVisibilityMapControl]);

    //console.log("B012524 settings", settings);
    useEffect(() => {
        console.log("B012524 settings", settings);
    }, [settings]);

    useEffect(() => {
        if (activeAnimation === "playing"){
            //console.log("indexA", indexA);
            //console.log("092022 datesAvailable", datesAvailable);
            let s = datesAvailable[indexA];
            let x = animationRasterDataSources.map((source: DataSourceRasterLayer, sourceIndex:number) => {
                if (source.dateString === s){
                    source.opacity = 1;
                }else{
                    source.opacity = 0;
                }
                return source;
            })
            setTimelineStatus(s);
            setAnimationRasterDataSources(x);
            if (indexA + 1 == datesAvailable.length){
                setActiveAnimationFromParent("paused");
                clearInterval(animationInterval);
            }else{
                setIndexA(prev => prev + 1 )
            }
        }else{
            clearInterval(animationInterval);
        }
    }, [timerInterval]);


    useEffect(() => {
        console.log("B032724 dateSelectorParameters", dateSelectorParameters);
        console.log("B032724 dateIndexSelected", dateIndexSelected);
        console.log("B032724 datesAvailable", datesAvailable);
        console.log("B032724 indexA", indexA);
        console.log("B032724 dateSelectorParameters.marks[dateIndexSelected[1]].props.children", dateSelectorParameters?.marks[dateIndexSelected[1]]?.props.children);
        setDateSelected(dateSelectorParameters?.marks[dateIndexSelected[1]]?.props.children ? `${settings.season_year}-${dateSelectorParameters?.marks[dateIndexSelected[1]]?.props.children}` : null);
    }, [dateIndexSelected, dateSelectorParameters, datesAvailable, indexA]);

    useEffect(() => {
        console.log("B032924 dateSelected", dateSelected);
    }, [dateSelected]);

    useEffect(() => {
        console.log("072825 fieldsDataSource", fieldsDataSource);
        // if page loads without a loc str, default to bounding box if there is data
        if (initViewportStr.length === 0 && width && height && fieldsDataSource) {
            zoomToExtent();
        }
    }, [width, height, fieldsDataSource]);

    useEffect(() => {
        if (!loadedTiles){
            assessAnimationLoad();
        }
    }, [loadedTiles]);

    useEffect(() => {
        if (width && height) {
            setSize({ width, height });
        }
    }, [width, height]);

    useEffect(() => {
        if (fields) {
            const polygonFeatures = fields.map((f: Field) => createFeatureFromField(f));
            const pointFeatures = fields.map((f: Field) => createCentroidFeatureFromField(f, null));

            const polygonFeatureCollection = createFeatureCollection(polygonFeatures);
            const pointFeatureCollection = createFeatureCollection(pointFeatures);

            setFieldsDataSource(polygonFeatureCollection);
            setCentroidDataSource(pointFeatureCollection);
        }
    }, [fields]);

    useEffect(() => {
        console.log("A060424 zones", zones);
        if (zones && zones.length > 0) {
            const polygonFeatures = zones[selectedZoneSetIndex].geojson?.features?.map((f: any) => f);
            console.log("A060424 polygonFeatures", polygonFeatures);
            const pointFeatures = zones[selectedZoneSetIndex].geojson?.features?.map((f: any) => {
                console.log("A060424 f",f);
                console.log("A060424 f.geometry, f.properties",f.geometry, f.properties);
                if (f.geometry){
                    let feat = createCentroidFeatureFromGeom(f.geometry, f.properties);
                    feat.properties = f.properties;
                    return feat;
                }else{
                    return null;
                }
            });
            console.log("A032924 pointFeatures", pointFeatures);
            const polygonFeatureCollection = createFeatureCollection(polygonFeatures);
            const pointFeatureCollection = createFeatureCollection(pointFeatures);
            console.log("A032924 polygonFeatureCollection", polygonFeatureCollection);
            console.log("A032924 pointFeatureCollection", pointFeatureCollection);
            setZonesDataSource(polygonFeatureCollection);
            setZonesPointDataSource(pointFeatureCollection);
        }else{
            /*if( zonesDataSource && zonesPointDataSource){
                let tempZonesPointDataSource:any = zonesPointDataSource as any;
                tempZonesPointDataSource.features = [];
                let tempZonesDataSource:any = zonesDataSource as any;
                tempZonesDataSource.features = [];
                setZonesDataSource(tempZonesDataSource);
                setZonesPointDataSource(tempZonesPointDataSource);
            }else{*/
                setZonesDataSource(null);
                setZonesPointDataSource(null);
            //}
        }
    }, [zones, selectedZoneSetIndex]);

    useEffect(() => {
        console.log("A032924 zonesPointDataSource", zonesPointDataSource);
        setZoneDataSourceIncrementor(prev => prev + 1);
    }, [zonesPointDataSource]);

    useEffect(() => {
        console.log("A032924 zonesDataSource", zonesDataSource);
        setZoneDataSourceIncrementor(prev => prev + 1);
    }, [zonesDataSource]);

    useEffect(() => {
        console.log("AA040624 selectedZoneName", selectedZoneName);
        console.log("AA040624 zones", zones);
        //setSelectedZoneId
        if (zones && zones[selectedZoneSetIndex] && selectedZoneName){
            console.log("AA040624 selectedZoneSetIndex", selectedZoneSetIndex);
            let selectedZone = zones[selectedZoneSetIndex].zones.find((z:any) => z.label === selectedZoneName);
            if (selectedZone){
                console.log("AA040624 selectedZone", selectedZone);
                setSelectedZoneId(selectedZone.entityId);
            }else{
                if (zones[selectedZoneSetIndex].zones.length > 0){
                    setSelectedZoneId(zones[selectedZoneSetIndex].zones[0]?.entityId);
                }else{
                    setSelectedZoneId(null);
                }
            }
        }else{
            setSelectedZoneId(null);
        }
    }, [selectedZoneName, zones, selectedZoneSetIndex]);

    useEffect(() => {
        console.log("A061824 zones", zones);
        console.log("A061824 selectedZoneSetIndex", selectedZoneSetIndex);
        if (zones && zones[selectedZoneSetIndex]){
            let selectedZoneSet = zones[selectedZoneSetIndex]
            console.log("A061824 selectedZoneSet", selectedZoneSet);
            console.log("A061824 B selectedZoneSet", selectedZoneSet);
            if (selectedZoneSet && selectedZoneSet.entityId){
                setSelectedZoneSetId(selectedZoneSet.entityId);
            }
        }else{
            console.log("A061824 selectedZoneSet", null);
            setSelectedZoneSetId(null);
        }
    }, [zones, selectedZoneSetIndex]);
    
    useEffect(() => {
        console.log("032224 fieldsDataSource", fieldsDataSource);
    }, [fieldsDataSource]);

    // set selected field when the field id is in the url
    useEffect(() => {
        console.log("010924 fields", fields)
        if (fields && field) {
            const selectedField = fields.find(f => f.id == field);
            const presentFieldId = selectedFieldId;
            if (selectedField /*&& width && height*/) {
                setSelectedFieldId(selectedField.id ? selectedField.id : null);
                setSelectedField(selectedField);
            }
        }
    }, [fields]);

    // zoom to a field when the field selection changes
    useEffect(() => {
        if (fields && field) {
            const selectedField = fields.find(f => f.id == field);
            const presentFieldId = selectedFieldId;
            if (selectedField && width && height) {
                setSelectedFieldId(selectedField.id ? selectedField.id : null);
                setSelectedField(selectedField);
                setViewport({ ...zoomToFeature(createFeatureFromField(selectedField), viewport, width, height, 85), transitionDuration: 500 });
            }
        }
    }, [field, selectedFieldIncrement]);

    useEffect(() => {
        if (!loading && dataSources) {
            console.log("C032724 dataSources", dataSources)
            const overlaySources: DataSourceRasterLayer[] = dataSources.filter((d: DataSourceRasterLayer) => {
                return (d.type == "overlay" && d.name !== "Brightness Index");
            })
            const baseSources: DataSourceRasterLayer[] = dataSources.filter((d: DataSourceRasterLayer) => d.type == "base")
            const overlayLayers: LayerVisibility[] = overlaySources.map((d: DataSourceRasterLayer) => {
                let _name = d.name;
                if (_name === "Productivity"){
                    _name = "Biomass";
                    console.log("050424 d", d);
                    setProductivityGlobalMaxMin({max:d.source.global_max, min:d.source.global_min});
                }
                if (_name === "Canopy Chlorophyll Concentration Index"){
                    _name = "CCCI";
                }
                if (_name === "Top of atmosphere NDRE"){
                    _name = "NDRE";
                }
                return {
                    layer_id: d.id,
                    layer_name: _name,
                    is_visible: d.is_visible, //d.name === "Productivity" ? true : d.is_visible,
                    order: d.order,
                    source: d.source,
                    override_max_min: (_name === "Biomass" || _name === "Stress") ? [0,100] : null,
                };
            });
            const baseLayers: LayerVisibility[] = baseSources.map((d: DataSourceRasterLayer) => {
                return {
                    layer_id: d.id,
                    layer_name: d.name,
                    is_visible: d.is_visible,
                    order: d.order,
                    source: d.source
                };
            });

            if (overlayVisibility.length === 0) {
                setOverlayVisible(baseLayers);
            }
            if (overlayVisibilityMapControl.length === 0) {
                setOverlayVisibleMapControl(overlayLayers);
            }

            setRasterDataSources(dataSources);
        }
    }, [dataSources, loading]);

    useEffect(() => {
        console.log("083123 overlayVisibilityMapControl", overlayVisibilityMapControl)
        console.log("083123 overlayVisibility", overlayVisibility)
        //let naip = overlayVisibility.find((item) => { return item.layer_id === NAIP_DATA_SOURCE });
        //updateLayersForExport(overlayVisibilityMapControl.concat(naip ? naip : []));
        updateLayersForExport(overlayVisibilityMapControl);
    }, [overlayVisibilityMapControl, overlayVisibility]);

    useEffect(() => {
        if (dateSelectorParameters && dateIndexSelected){
            console.log("B062024 dateSelectorParameters", dateSelectorParameters);
            console.log("B062024 dateIndexSelected", dateIndexSelected);
            let _sequence = dateSelectorParameters.sequence;
            let min_date = dateSelectorParameters.min_date;
            let max_date = dateSelectorParameters.max_date;
            for (let s in _sequence){
                if (parseInt(_sequence[s][0]) == dateIndexSelected[0]){
                    min_date = s;
                } 
                if (parseInt(_sequence[s][0]) == dateIndexSelected[2]){
                    max_date = s;
                } 
            };
            let momentDateMin = moment(min_date).format('YYYY-MM-DD');
            var minDate:any = new Date(momentDateMin);
            var minDaysSinceEpoch = Math.floor(minDate/8.64e7);
            let momentDateMax = moment(max_date).format('YYYY-MM-DD');
            var maxDate:any = new Date(momentDateMax);
            var maxDaysSinceEpoch = Math.floor(maxDate/8.64e7);
            setDownloadDateRange({
                "yearStart": minDaysSinceEpoch,
                "min_date": min_date,
                "yearEnd": maxDaysSinceEpoch,
                "max_date": max_date,
            });
        }
        
    }, [dateSelectorParameters, dateIndexSelected]);

    const delayedUrlUpdate = React.useCallback(_.debounce((url: any) => history.replace(url), 250), []);

    const onInteractionStateChange = (e: any) => {
        console.log("013024 e", e);
        if (!e.isDragging && !e.isPanning && !e.isZooming) {
            console.log("013024 onInteractionStateChange", viewport)
            const str = `@${viewport.latitude.toFixed(5)},${viewport.longitude.toFixed(5)},${viewport.zoom.toFixed(2)}`;
            const url = field ?
                generatePath(`${FIELD_APP_URL}/:vp/fields/:field`, { vp: str, field: field }) :
                generatePath(`${FIELD_APP_URL}/:vp/fields`, { vp: str });

            delayedUrlUpdate(url);
            /* Will get the bbox of the viewport, may be useful if we want to clip/zip/ship to viewport instead of field bnds.
            if (map) {
                console.log("083023 map", map)
                console.log("083023 map.getBounds()", map.getBounds())
                let bounds = map.getBounds();
                let bbox = [bounds._ne.lat, bounds._ne.lng, bounds._sw.lat, bounds._sw.lng];
            }
            */
        }
    }

    const zoomToExtent = React.useCallback(() => {
        if (width && height && fieldsDataSource) {
            deselectField();
            console.log("011224 zoomToExtent event")
            setPauseClickEvents(true); // Lock click events while zooming
            setTimeout(function() {
                console.log("011224 setTimeout COMPLETE")
                setPauseClickEvents(false);
            },500);
            setViewport({ ...zoomToFeatures(fieldsDataSource, viewport, width, height, 100), transitionDuration: 500 });
        }
    }, [fieldsDataSource, viewport, width, height]);

    const onSelectField = React.useCallback((field: Field) => {
        console.log("083023 onSelectField", field)
        setSelectedFieldIncrement(prev => prev + 1); 
        if (match) {
            history.push(`${match.url}${match.url.endsWith("/") ? "" : "/"}${field.id}`);
        }
    }, []);

    const deselectField = () => {
        setSelectedFieldId(null);
        setSelectedField(null);

        if (match) {
            history.push(`${match.url}`);
        }
    };

    const generateTileURLsAtDates = (_rasterDataSourceToAnimate:DataSourceRasterLayer, _datesAvailable:Array<string>) => {
        const keyFramedRasterDataSourceToAnimate = _datesAvailable.map((dateString: string, index:number) => { 
            let _tempDS = {..._rasterDataSourceToAnimate};
            console.log("A062725 _datesAvailable", _datesAvailable);
            _tempDS["opacity"] = 0.0;
            _tempDS["dateString"] = dateString;
            _tempDS.id = `${_tempDS.id}_${index}`;
            _tempDS.uuid = `${_tempDS.uuid}_${index}`;
            _tempDS.tiles = getTileUrlTemplate(
                _tempDS.source, // 1
                _tempDS.source.stac_address!, // 2 
                _tempDS.source.collection_id!, // 3
                'footprint_id', // 4
                _tempDS.source.min_date, // 5
                dateString, // 6
                _tempDS.source.date_display_resolution!, // 7
                "template-singledate-p1", // 8
                null, // 9 
                "global", // 10
                settings.season_year // 11
            )
            return _tempDS;
        })
        return keyFramedRasterDataSourceToAnimate;
    };

    const loadAnimationTiles = (_dateSelectorParameters:any) => {
        let _datesAvailable:Array<string> = [];
        let _sequence = _dateSelectorParameters.sequence;

        for (let s in _sequence){
            if ((parseInt(_sequence[s][0]) >= dateIndexSelected[0]) && (parseInt(_sequence[s][0]) <= dateIndexSelected[2])){
                _datesAvailable.push(s)
            }
        };
        setDatesAvailable(_datesAvailable);
        let visibleRasterDataSources = rasterDataSources.filter((d: DataSourceRasterLayer) => {
            let ds = overlayVisibilityMapControl.find((item) => { return item.layer_id === d.id });
            return (ds?.is_visible == true);
        });
        let tempArray:any = [];
        visibleRasterDataSources.forEach((sr: DataSourceRasterLayer, index:number) => {
            let c = generateTileURLsAtDates(sr, _datesAvailable)
            tempArray = tempArray.concat(c);
        })
        setAnimationRasterDataSources(tempArray);
        setLoadedTiles(false);
    };

    const onHoverField = React.useCallback((field: Field) => {
        setHoverFieldId(field ? field.id! : null);
    }, []);

    const onClick = (event:any) => {
        console.log("011224 onClick pauseClickEvents", pauseClickEvents)
        if (pauseClickEvents){
            console.log("011224 onClick event REJECTED", event.features[0])
        }else{
            console.log("011224 onClick event ACCEPTED", event.features[0])
            const { features } = event;
            console.log("Clicked on a feature:", features);
            const clickedZoneFeature = features && features.find((f:any) => f.layer.id === "circle-background");

            if (clickedZoneFeature) {
              console.log("Clicked on a zone circle:", clickedZoneFeature);
              // Perform your desired action here, for example:
              const clickedZoneValue = clickedZoneFeature.properties.label;
              console.log("Z040624 C");
              setSelectedZoneName(clickedZoneValue);
            }else{
                const feature = event.features[0];

                if (!feature) {
                    return;
                }

                if (match) {
                    history.push(`${match.url}/${feature.properties.id}`);
                }
            }
        }
    };

    const onHover = useCallback(event => {
        const field = event.features && event.features[0];

        setHoverInfo({
            longitude: event.lngLat[0],
            latitude: event.lngLat[1],
            fieldName: field && field.properties.name
        });
    }, []);

    function assessAnimationLoad(){
        if (map){
            if (map.loaded() == true && map.areTilesLoaded() == true) {
                console.log("load is complete");
                setLoadedTiles(true);
                playAnimation(datesAvailable);
            } else {
                console.log("load is incomplete");
                setTimeout(function() {
                    assessAnimationLoad();
                },500);
            }
        }
    }

    function setActiveAnimationFromParent(_settingToApply:string){
        setActiveAnimation(_settingToApply)
    }
    
    function playAnimation(_datesAvailable:Array<string>){
        //console.log("_datesAvailable",_datesAvailable);
        //console.log("datesAvailable",datesAvailable);
        //console.log("animationRasterDataSources",animationRasterDataSources);
        if (animationInterval){
            clearInterval(animationInterval);
        }
        //console.log("indexA + 1 == datesAvailable.length", indexA + 1, datesAvailable.length);
        if (indexA + 1 >= datesAvailable.length){
            //console.log("setIndexA(0);");
            setIndexA(0);
        }
        setAnimationInterval(setInterval(myTimer, 1000));

        function myTimer() {
            setTimerInterval(prev => prev + 1 )
        }
    }

    const onInfoDrawerClose = useCallback(() => {
        deselectField();
        clearLayers();
    }, []);

    const onEditZonsetClicked = (event:any, mode:string) => {
        if(match) {
            //onSelectField(field);
            if (selectedField){
                if(mode == "edit"){
                    history.push(`${match.url}/${selectedField?.id}/newzone`); // history.push(`${match.url}/${selectedField?.id}/${selectedZoneSetId}/editzone`);
                }else{
                    history.push(`${match.url}/${selectedField?.id}/newzone`);
                }
            }else{
                // This shouldnt be possible but just in case.
                console.log("onEditZonsetClicked selectedField is null")
                //history.push(`${match.url}/${selectedZoneSetId}/editzone`);
            }
        }
    }
      
    const selectedId = selectedFieldId || '';
    const hoverId = hoverFieldId || '';
    const hoverFieldName = (hoverInfo && hoverInfo.fieldName) || '';

    const selectedFilter = useMemo(() => ['in', 'id', selectedId], [selectedId]);
    const hoverfilter = useMemo(() => ['in', 'id', hoverId], [hoverId]);

    const infoChildren = useMemo(() => {
        if (!selectedField) {
            return;
        }

        return (
            <>
                <LayerVisibilityControlPanel 
                    showDatePicker={true} 
                    layers={overlayVisibilityMapControl} 
                    onChange={(visibility: LayerVisibility[]) =>  setOverlayVisibleMapControl(visibility)} 
                    context={"sidebar"}
                    dateSelectorParameters={dateSelectorParameters}
                    setDateSelectorParameters={setDateSelectorParameters} 
                    dateIndexSelected={dateIndexSelected}
                    setDateIndexSelected={setDateIndexSelected}
                />
                { showDownload && <FieldDownload onCancel={() => setShowDownload(false)} /> }
                <FieldStatTable
                    field={selectedField}
                    name="Stress"
                    metric="stress"
                    description={STRESS_DESC}
                    changeInverted={true}
                    selectedDate={dateSelected}
                />
                <DwFieldStatWidget
                    field={selectedField}
                    name="Biomass"
                    description={PRODUCTIVITY_DESC}
                    metric="productivity" 
                    dateSelected={dateSelected}/>
                <DwFieldStatWidget
                    field={selectedField}
                    name="Stress"
                    metric="stress"
                    description={STRESS_DESC}
                    changeInverted={true} 
                    dateSelected={dateSelected}/>
                <ZoneManagementSelector
                    field={selectedField}
                    setZones={(zones: Zone[]) => setZones(zones)}
                    zones={zones}
                    selectedZoneName={selectedZoneName}
                    setSelectedZoneName={setSelectedZoneName}
                    selectedZoneSetId={selectedZoneSetId}
                    setSelectedZoneSetId={setSelectedZoneSetId}
                    selectedZoneSetIndex={selectedZoneSetIndex}
                    setSelectedZoneSetIndex={setSelectedZoneSetIndex}
                    selectedDate={dateSelected}
                    zoneData={zoneData}
                    setZoneData={setZoneData}
                    onEditZonsetClicked={onEditZonsetClicked}
                    zonesVisibility={zonesVisibility}
                    setZonesVisibility={setZonesVisibility}
                />
                <SoilInfoWidget field={selectedField} customCSS={null}/>
            </>
        )
    }, [selectedField, showDownload, selectedZoneName, zones, selectedZoneSetIndex, zoneData, zonesVisibility]);

    const zoneChildren = useMemo(() => {
        if (!selectedField) {
            return;
        }

        return (
            <>
                <ZoneManagementBBCard
                    field={selectedField}
                    setZones={(zones: Zone[]) => setZones(zones)}
                    zones={zones}
                    selectedZoneName={selectedZoneName}
                    setSelectedZoneName={setSelectedZoneName}
                    selectedZoneSetId={selectedZoneSetId}
                    selectedZoneSetIndex={selectedZoneSetIndex}
                    selectedDate={dateSelected}
                    zoneData={zoneData}
                    setZoneData={setZoneData}
                />
                <DwZoneStatWidget
                    field={selectedField}
                    name="Biomass"
                    description={PRODUCTIVITY_DESC}
                    metric="productivity" 
                    dateSelected={dateSelected}
                    selectedZoneName={selectedZoneName}
                    selectedZoneId={selectedZoneId}
                    selectedZoneSetId={selectedZoneSetId}
                    zones={zones}
                    zoneData={zoneData}
                    setZoneData={setZoneData}
                    productivityMaxMin={productivityGlobalMaxMin}
                    />
                <DwZoneStatWidget
                    field={selectedField}
                    name="Stress"
                    metric="stress"
                    description={STRESS_DESC}
                    changeInverted={true} 
                    dateSelected={dateSelected}
                    selectedZoneName={selectedZoneName}
                    selectedZoneId={selectedZoneId}
                    selectedZoneSetId={selectedZoneSetId}
                    zones={zones}
                    zoneData={zoneData}
                    setZoneData={setZoneData}
                />
            </>
        )
    }, [selectedField, selectedZoneName, zones, selectedZoneSetId, selectedZoneId, dateSelected, zoneData, selectedZoneSetIndex]);
    
    useEffect(() => {
       console.log("083023 selectedField", selectedField)
        if (selectedField){
            updateFieldSelected(selectedField)
        }
    }, [selectedField]);

    return (
        <div className="d-flex h-100 position-relative">
            <div className="bg-light border border-bottom-0 border-dark border-left-0 border-top-0 d-flex flex-column flex-shrink-0 rounded-left text-dark" style={{ width: '200px' }}>
                <FieldList
                    title="Blocks"
                    fields={fields}
                    selectedId={selectedFieldId}
                    onSelectField={onSelectField}
                    onHoverField={onHoverField}
                    overlayVisibility={overlayVisibilityMapControl}
                    setOverlayVisible={setOverlayVisibleMapControl}
                    dateSelectorParameters={dateSelectorParameters}
                    setDateSelectorParameters={setDateSelectorParameters}
                    dateIndexSelected={dateIndexSelected}
                    setDateIndexSelected={setDateIndexSelected}
                    dateSelected={dateSelected}
                />
            </div>
            {selectedField &&
                <div className="bg-light border border-bottom-0 border-left-0 border-right border-secondary border-top-0 d-flex flex-column flex-shrink-0 rounded-right text-dark" style={{ width: '295px' }}>
                    <InfoDrawer 
                        field={selectedField} 
                        onClose={onInfoDrawerClose}
                        setShowDownload={setShowDownload}
                    >
                        {infoChildren}
                    </InfoDrawer>
                </div>
            }
            <div className="flex-fill bg-white" ref={mapContainerRef}>
                <MapGL
                    {...viewport}
                    width='100%'
                    height='100%'
                    mapStyle={ESRI_BASE}
                    onClick={onClick}
                    onHover={onHover}
                    interactiveLayerIds={['field-fill', 'circle-background']}
                    //interactiveLayerIds={ zonesPointDataSource ? ['field-fill', 'circle-background'] : ['field-fill']}
                    onInteractionStateChange={(s: any) => onInteractionStateChange(s)}
                    onViewportChange={(v: ViewState) => setViewport(v)}
                    ref={(ref:any) => ref && setMap(ref.getMap())}
                >
                    {
                        ['baselayer', 'fieldlayer', 'vectorlayer', 'vectorbaselayer'].map(name => {
                            return (<Layer
                                key={name}
                                id={'GROUP_' + name}
                                type='background'
                                layout={{ visibility: 'none' }}
                                paint={{}}
                            />)
                        })
                    }

                    {rasterDataSources ? _.orderBy(rasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer) => (
                        <Source key={s.uuid} id={s.uuid} type='raster' tileSize={256} tiles={s.tiles}>
                            <Layer
                                key={s.uuid}
                                beforeId="GROUP_baselayer"
                                id={s.uuid}
                                type='raster'
                                source={s.uuid}
                                maxzoom={s.max_zoom} minzoom={s.min_zoom}
                                layout={{ visibility: ((s.type === "overlay") && (activeAnimation === "playing" || activeAnimation === "paused") && loadedTiles) 
                                                ? 'none' 
                                                : overlayVisibility.concat(overlayVisibilityMapControl).find((item) => { return item.layer_id === s.id })?.is_visible 
                                                    ? 'visible' 
                                                    : 'none'
                                        }}
                            />
                        </Source>)
                    ) : null}

                    {animationRasterDataSources ? _.orderBy(animationRasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer, index:number) => (
                        <Source key={`${s.uuid}`} id={`${s.uuid}`} type='raster' tileSize={256} tiles={s.tiles}>
                            <Layer
                                key={`${s.uuid}`}
                                beforeId="GROUP_baselayer"
                                id={`${s.uuid}`}
                                type='raster'
                                source={s.uuid}
                                maxzoom={s.max_zoom}
                                minzoom={s.min_zoom}
                                layout={{ visibility: (activeAnimation === "playing" || activeAnimation === "paused")
                                        ? 'visible'
                                        : 'none'
                                    }}
                                paint={{
                                    'raster-opacity': s.opacity,
                                }}
                            />
                        </Source>)
                    ) : null}
                    {rasterLayers ? _.orderBy(rasterLayers, ['order'], ['desc']).map((s: DataSourceRasterLayer) => (
                        <Source key={s.id} id={s.id} type='raster' tileSize={256} scheme="tms" tiles={s.tiles}>
                            <Layer
                                key={s.id}
                                beforeId="GROUP_fieldlayer"
                                id={s.id}
                                type='raster'
                                source={s.id}
                                maxzoom={s.max_zoom}
                                minzoom={s.min_zoom}
                                layout={{ visibility: 'visible' }}
                            />
                        </Source>)
                    ) : null}
                    {/*
                    <Source type="geojson" data={CA_BOUNDARY}>
                        <Layer beforeId="GROUP_vectorbaselayer" {...stateBorderLayer} />
                    </Source>*/}
                    
                    { zonesDataSource && zonesPointDataSource && zonesVisibility
                    ?<>
                    <Source 
                        //key={`zonesDataSourceID_${zoneDataSourceIncrementor}`}
                        type="geojson" data={zonesDataSource} id="zonesDataSourceID">
                        <Layer beforeId="GROUP_vectorbaselayer" {...zonesLayer} minzoom={10} />
                        <Layer 
                            beforeId="GROUP_vectorbaselayer" 
                            {...zonesBorderLayer} 
                            paint={{
                                'line-color': '#000',
                                'line-width': [
                                    'case',
                                    ['==', ['get', 'label'], selectedZoneName], 
                                    4,
                                    2, 
                                    ], 
                            }}
                            minzoom={10} 
                        />
                    </Source>
                    <Source 
                        //key={`zonesPointDataSourceID_${zoneDataSourceIncrementor}`}
                        type="geojson" data={zonesPointDataSource} id="zonesPointDataSourceID">
                        {/* Circle layer for background */}
                        <Layer
                            id="circle-background"
                            beforeId="zones-label" 
                            type="circle"
                            source="zonesPointDataSourceID"
                            paint={{
                                "circle-radius": 15, 
                                "circle-color": [
                                    'case',
                                    ['==', ['get', 'label'], selectedZoneName], 
                                    '#000',
                                    '#fff', 
                                  ], 
                                "circle-opacity": 0.75,
                                "circle-stroke-color": [
                                    'case',
                                    ['==', ['get', 'label'], selectedZoneName],
                                    '#fff',
                                    '#000',
                                  ], 
                                "circle-stroke-width": 1,
                            }}
                        />
                        <Layer
                            type="symbol"
                            id="zones-label"
                            beforeId="GROUP_vectorbaselayer"
                            source="zonesPointDataSourceID" 
                            layout={{
                                "text-field": ["get", "label"], 
                                "text-font": ["Open Sans Regular","Arial Unicode MS Regular"], 
                                "text-size": 16, 
                                "text-anchor": "center", 
                                }}
                            paint={{
                               "text-color": [
                                    'case',
                                    ['==', ['get', 'label'], selectedZoneName], 
                                    '#fff',
                                    '#000', 
                                ],  
                               //"text-halo-color": "#000", 
                               //"text-halo-width": 1, 
                            }}
                            minzoom={10} // Adjust zoom levels as necessary
                        />
                    </Source>
                    {/*<Source type="geojson" data={fieldsDataSource}>
                        <Layer beforeId="GROUP_vectorlayer" {...fieldsLayer} minzoom={10} />
                        <Layer beforeId="GROUP_vectorlayer" {...fieldsBorderLayer} minzoom={10} />
                        <Layer beforeId="GROUP_vectorlayer" {...fieldsSelectedBorderLayer} filter={selectedFilter} minzoom={10} />
                        {showHighlightLayer && <Layer beforeId="GROUP_vectorlayer" {...fieldsHighlightLayer} filter={hoverfilter} minzoom={10} />}
                    </Source>*/}
                    <Source type="geojson" data={fieldsDataSource}>
                        <Layer minzoom={10} {...fieldsLayer} beforeId="GROUP_vectorlayer"/>
                        <Layer minzoom={10} {...fieldsBorderLayer} beforeId="GROUP_vectorlayer"/>
                        <Layer filter={selectedFilter} minzoom={10} {...fieldsSelectedBorderLayer} beforeId="GROUP_vectorlayer"
                        />
                        {showHighlightLayer && ( <Layer filter={hoverfilter} minzoom={10} {...fieldsHighlightLayer} beforeId="GROUP_vectorlayer"/>
                        )}
                        {/*<Layer id="fields-labels" type="symbol" minzoom={10} beforeId="GROUP_vectorlayer"
                            layout={{
                            "text-field": ["get", "name"],
                            "text-size": 12,
                            "text-anchor": "center",
                            "text-offset": [0, 0]
                            }}
                            paint={{
                            "text-color": "#000",
                            "text-halo-color": "#fff",
                            "text-halo-width": 1.5
                            }}
                        />*/}
                    </Source>
                    </>
                    : <Source type="geojson" data={fieldsDataSource}>
                        <Layer minzoom={10} {...fieldsLayer} beforeId="GROUP_vectorlayer"/>
                        <Layer minzoom={10} {...fieldsBorderLayer} beforeId="GROUP_vectorlayer"/>
                        <Layer filter={selectedFilter} minzoom={10} {...fieldsSelectedBorderLayer} beforeId="GROUP_vectorlayer"
                        />
                        {showHighlightLayer && ( <Layer filter={hoverfilter} minzoom={10} {...fieldsHighlightLayer} beforeId="GROUP_vectorlayer"/>
                        )}
                        {/*<Layer id="fields-labels" type="symbol" minzoom={10} beforeId="GROUP_vectorlayer"
                            layout={{
                            "text-field": ["get", "name"],
                            "text-size": 12,
                            "text-anchor": "center",
                            "text-offset": [0, 0]
                            }}
                            paint={{
                            "text-color": "#000",
                            "text-halo-color": "#fff",
                            "text-halo-width": 1.5
                            }}
                        />*/}
                    </Source>}
                    <Source
                        type="geojson"
                        data={centroidDataSource}
                        cluster={true}
                        clusterMaxZoom={8}
                        clusterRadius={40}>
                        <Layer beforeId="GROUP_vectorlayer" {...clusterLayer} maxzoom={10} />
                        <Layer beforeId="GROUP_vectorlayer" {...unclusteredLayer} maxzoom={10} />
                    </Source>
                    <Source type="geojson" id="field-centroids" data={centroidDataSource}>
                        <Layer
                            id="field-labels"
                            type="symbol"
                            beforeId="GROUP_vectorlayer"
                            minzoom={12}
                            layout={{
                                "text-field": ["get", "name"],
                                "text-font": ["Open Sans Regular","Arial Unicode MS Regular"],
                                "text-size": 15,
                                "text-anchor": "center",
                                "text-offset": [0, 0],
                                "symbol-placement": "point"
                            }}
                            paint={{
                                "text-color": "#000",
                                "text-halo-color": '#fff', //"#b0ede0",
                                "text-halo-width": 1,
                                "text-halo-blur": 0.5
                            }}
                        />
                    </Source>
                    <NavigationControl style={{left: 10, top: 10}} showCompass={false} />
                    <ScaleControl 
                        style={{right: (selectedZoneName && !rightInfoDrawerOpen) ? 50 : 10, top: 10}} 
                        maxWidth={200} 
                        unit={preferredLenAbbr === "in" ? "imperial" : "metric"} // Change to "metric" or "imperial" as needed
                    />
                    <ZoomExtentTool zoomToExtent={zoomToExtent} />

                    {(hoverFieldName && !selectedField) && (
                        <Popup
                            longitude={hoverInfo.longitude}
                            latitude={hoverInfo.latitude}
                            closeButton={false}
                            className="field-info"
                        >
                            {hoverFieldName}
                        </Popup>
                    )}
                </MapGL>
                <div style={{position: 'absolute', bottom:'unset', top: selectedZoneName ? '45px' : '37px', right:'10px'}}>
                    <LayerVisibilityControlPanel 
                        showDatePicker={true} 
                        layers={overlayVisibility} 
                        onChange={(visibility: LayerVisibility[]) => setOverlayVisible(visibility)}
                        initiallyOpen={false}
                    />
                </div>
                <div style={{
                    width:'100%', 
                    position: 'relative', 
                    top:'unset', 
                    bottom:'110px', 
                    right:'0px', 
                    height: '110px', 
                    display:"flex",
                    pointerEvents:'none',
                }}>
                    <LayerVisibilityControlPanel 
                        showDatePicker={true} 
                        layers={overlayVisibilityMapControl} 
                        onChange={(visibility: LayerVisibility[]) => setOverlayVisibleMapControl(visibility)} 
                        context={"timeline"}
                        dateSelectorParameters={dateSelectorParameters}
                        setDateSelectorParameters={setDateSelectorParameters} 
                        dateIndexSelected={dateIndexSelected}
                        setDateIndexSelected={setDateIndexSelected}
                        loadAnimationTiles={loadAnimationTiles}
                        loadedTiles={loadedTiles}
                        timelineStatus={timelineStatus}
                        setTimelineStatus={setTimelineStatus}
                        activeAnimation={activeAnimation}
                        setActiveAnimationFromParent={setActiveAnimationFromParent}
                        indexA={indexA}
                        setIndexA={setIndexA}
                    />       
                    
                </div>
            </div>
            { (selectedZoneName && rightInfoDrawerOpen)
                ? <div className="bg-light border border-bottom-0 border-top-0 border-right-0 d-flex flex-column flex-shrink-0 rounded-left text-dark" style={{ width: '300px', zIndex:2}}>
                    <InfoDrawer field={null} onClose={() => { setRightInfoDrawerOpen(!rightInfoDrawerOpen) }} showClose={true} side={"right"}>
                        {zoneChildren}
                    </InfoDrawer>
                </div>
                : selectedZoneName 
                    ? <div 
                        className="closed-widget-button"
                        style={{
                            right:"12.5px"
                        }}
                    >
                        <Button 
                            onClick={() => { setRightInfoDrawerOpen(!rightInfoDrawerOpen) }}
                            className="rounded-circle" variant="light" size="sm" title="Close Card" style={{cursor:'pointer', height:'30px', width:'30px'}}>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </Button>
                    </div>
                    : <></>
            }
        </div>
    );
}
